// client/src/components/pages/LexiStar/PlanDispatch/useWorkshopEvents.ts (ST-00249)
//
// The planned-maintenance and breakdown events of the trucks in the grid around their planned
// starts (GET /api/lexistar/maintenance/events), so a row whose truck is in the workshop — or
// broken down — at its planned start warns "In the workshop: Brake service until 14:00" (never a
// block; planLogic.validateRow, the dispatch preview). One request per window: from the earlier of
// now and the first planned start (open events always reach now) to the last planned start; again
// when the window grows or after a minute. Without the module (503) or the permission (403): none.

import { useEffect, useMemo, useState } from 'react';
import { listEvents } from '../../../../services/lexistarMaintenance';
import type { TimeEvent } from '../../../../types/lexistar-maintenance';
import { workshopWarning } from '../Maintenance/maintenanceLogic';

const MIN = 60000;
const REFRESH_MS = 60000;

/** The [from, to] instants to ask for: whole 30-minute steps so small edits reuse the result. */
export function workshopWindow(starts: Array<Date | string | null | undefined>, now: number): { from: string; to: string } | null {
  const ms = starts
    .map((s) => (s ? new Date(s).getTime() : NaN))
    .filter((t) => Number.isFinite(t));
  if (!ms.length) return null;
  const step = 30 * MIN;
  const from = Math.floor((Math.min(now, ...ms) - MIN) / step) * step;
  const to = Math.ceil((Math.max(...ms) + MIN) / step) * step;
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}

export default function useWorkshopEvents(
  rows: Array<{ planned_start?: Date | string | null; asset_id?: string | null }>,
  { enabled = true }: { enabled?: boolean } = {}
): { events: TimeEvent[]; warningFor: (assetId: string | null | undefined, at: Date | string | null | undefined) => string | null } {
  const [events, setEvents] = useState<TimeEvent[]>([]);
  const [tick, setTick] = useState(0);
  const withTruck = rows.filter((r) => r.asset_id);
  const win = workshopWindow(
    withTruck.map((r) => r.planned_start),
    Math.floor(Date.now() / (30 * MIN)) * 30 * MIN
  );
  const key = win ? `${win.from}|${win.to}` : '';

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!enabled || !key) {
      setEvents([]);
      return undefined;
    }
    let live = true;
    const [from, to] = key.split('|');
    listEvents({ category: ['planned_maintenance', 'breakdown'], state: ['booked', 'open', 'closed'], from, to, limit: 1000 })
      .then((res) => {
        if (live) setEvents(Array.isArray(res?.events) ? res.events : []);
      })
      .catch(() => {
        if (live) setEvents([]);
      });
    return () => {
      live = false;
    };
  }, [enabled, key, tick]);

  const warningFor = useMemo(
    () => (assetId: string | null | undefined, at: Date | string | null | undefined) =>
      workshopWarning(events, assetId, at ? new Date(at).getTime() : null)?.text ?? null,
    [events]
  );
  return useMemo(() => ({ events, warningFor }), [events, warningFor]);
}
